// Section 16 trust rows applied by the society phase (the rationing rows are the rationing
// phase's, the justice rows the crime phase's): a promised build finished (+3 everyone, per
// building), a festival (+4 everyone, festival.ts), the harsh justice norm's -1 per season for
// Gentle and Devout people, and the refusal below settlement trust 25.
import { clamp } from '../../core/math';
import { peopleSorted, settlementTrust } from '../../core/queries';
import { sortedValues } from '../../core/sorted';
import type { HouseholdId, Person, PhaseContext, TrustCause } from '../../core/types';
import { num } from '../shared/value';

export type TrustTally = Map<TrustCause, { delta: number; people: number }>;

/** Apply one trust row to a drafted person through the `trustChange` target. */
export function applyTrust(
  ctx: PhaseContext,
  p: Person,
  cause: TrustCause,
  tally: TrustTally,
  times = 1,
): void {
  const delta = num(ctx, { kind: 'trustChange', personId: p.id, cause }) * times;
  if (delta === 0) return;
  p.trust = clamp(p.trust + delta, 0, ctx.cfg.people.statMax);
  const row = tally.get(cause) ?? { delta: 0, people: 0 };
  row.delta += delta;
  row.people += 1;
  tally.set(cause, row);
}

export function reportTrust(ctx: PhaseContext, tally: TrustTally): void {
  for (const [cause, row] of [...tally.entries()].sort(([a], [b]) => (a < b ? -1 : 1)))
    ctx.report.add({ kind: 'trust', cause, delta: row.delta / row.people, people: row.people });
}

/** Buildings completed this season that the player had promised (Section 16, B's flag). */
function promisedBuilds(ctx: PhaseContext): number {
  let n = 0;
  for (const e of ctx.state.currentReport?.entries ?? [])
    if (e.kind === 'building-complete' && e.promised === true) n++;
  return n;
}

export function seasonalTrust(ctx: PhaseContext, tally: TrustTally): void {
  const builds = promisedBuilds(ctx);
  for (const snap of peopleSorted(ctx.state)) {
    const harsh = num(ctx, { kind: 'trustChange', personId: snap.id, cause: 'norm-harsh-justice' });
    if (builds === 0 && harsh === 0) continue;
    const p = ctx.draft.people[snap.id];
    if (p === undefined) continue;
    if (builds > 0) applyTrust(ctx, p, 'promised-build', tally, builds);
    if (harsh !== 0) applyTrust(ctx, p, 'norm-harsh-justice', tally);
  }
}

/**
 * Section 16: below settlement trust 25 a refusal can occur (config.trust.refusalChance): the
 * workers of the worst-treated households (lowest mean trust, config.trust.refusalHouseholdShare,
 * at least one) stop working next season (age contributor, `society:refusal`).
 */
export function maybeRefuse(ctx: PhaseContext): void {
  const t = ctx.cfg.trust;
  if (settlementTrust(ctx.state) >= t.refusalBelow) return;
  if (!ctx.rng.happened(ctx.rng.chance(t.refusalChance))) return;
  const rows: { id: HouseholdId; trust: number }[] = [];
  for (const h of sortedValues(ctx.state.households)) {
    const members = h.memberIds
      .map((id) => ctx.state.people[id])
      .filter((p): p is Person => p !== undefined);
    if (!members.some((p) => p.job !== 'rest')) continue;
    rows.push({ id: h.id, trust: members.reduce((a, p) => a + p.trust, 0) / members.length });
  }
  if (rows.length === 0) return;
  rows.sort((a, b) => a.trust - b.trust || (a.id < b.id ? -1 : 1));
  const count = Math.max(1, Math.ceil(rows.length * t.refusalHouseholdShare));
  const householdIds = rows
    .slice(0, count)
    .map((r) => r.id)
    .sort();
  ctx.draft.counters.refusingHouseholds = householdIds;
  ctx.draft.counters.lastRefusalTurn = ctx.season.turn;
  ctx.report.add({ kind: 'refusal', householdIds });
}
